import type {
  AvailableVariantT,
  SkuVariantT,
} from '@lambda/apis/src/catalog-v2/types';

/**
 * `availableVariants` spells out every variant name and value on every SKU, so
 * a 31-SKU product repeats the same 17 pairs 279 times — 33 KB of a 38 KB prop
 * in the server-rendered `__NEXT_DATA__`.
 *
 * Packing sends each distinct pair once and leaves an index behind on each SKU.
 * `unpackAvailableVariants` restores the original objects on the client, so
 * nothing downstream of the page needs to know this happened.
 */
export type PackedAvailableVariantsT = {
  /** Each distinct variant pair, in first-seen order. */
  dictionary: SkuVariantT[];
  /** The SKUs, with `variants` as indices into `dictionary`. */
  skus: (Omit<AvailableVariantT, 'variants'> & { variants: number[] })[];
};

export const packAvailableVariants = (
  available: AvailableVariantT[],
): PackedAvailableVariantsT => {
  const dictionary: SkuVariantT[] = [];
  const indexByPair = new Map<string, number>();

  // Keyed on the whole pair rather than slug+value, so two pairs that differ in
  // `sequence` or `displayValue` stay distinct and the round trip is lossless.
  const indexOf = (pair: SkuVariantT): number => {
    const key = JSON.stringify(pair);
    const seen = indexByPair.get(key);

    if (seen !== undefined) {
      return seen;
    }

    const index = dictionary.push(pair) - 1;

    indexByPair.set(key, index);

    return index;
  };

  return {
    dictionary,
    skus: available.map((sku) => ({
      ...sku,
      variants: sku.variants.map(indexOf),
    })),
  };
};

export const unpackAvailableVariants = (
  packed: PackedAvailableVariantsT | undefined,
): AvailableVariantT[] => {
  if (!packed?.skus) {
    return [];
  }

  return packed.skus.map((sku) => ({
    ...sku,
    variants: sku.variants.map((index) => packed.dictionary[index]),
  }));
};
